import {
  Component,
  effect,
  EventEmitter,
  OnInit,
  Output,
  signal,
} from "@angular/core";
import { IClippingStudioFindClipRepository } from "app/js/src/domain/clipping-studio/i-clipping-studio-find-clip-repository";
import {
  FindClipSuggestedClip,
  FindClipSuggestedEdit,
} from "app/js/src/domain/clipping-studio/clipping-studio-find-clip";
import { ClippingStudioManagerService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-manager.service";
import {
  TimelineThumbnailVttService,
  ThumbnailCue,
} from "app/js/src/infrastructure/clipping-studio/timeline-thumbnail-vtt.service";

const USER_FACING_ERROR_MESSAGE =
  "Something went wrong. Please try again later.";

@Component({
  selector: "evrideo-clipping-studio-find-clip-panel",
  templateUrl: "./clipping-studio-find-clip-panel.component.html",
  styleUrls: ["./clipping-studio-find-clip-panel.component.scss"],
})
export class ClippingStudioFindClipPanelComponent implements OnInit {
  description = "";
  duration = "30s";

  readonly recentPrompts = signal<string[]>([]);
  readonly promptsLoading = signal(false);
  readonly promptsError = signal<string | null>(null);

  isGenerating = false;
  isGeneratingEdit = false;
  suggestedClips: FindClipSuggestedClip[] = [];
  suggestedEdit: FindClipSuggestedEdit | null = null;
  errorMessage: string | null = null;

  /** Emitted when a segment was added so the parent can capture thumbnails. */
  @Output() segmentAdded = new EventEmitter<string>();

  /** Cues for current media thumbnail sprite (same as timeline). */
  thumbnailCues: ThumbnailCue[] | null = null;
  private thumbnailVttUrl: string | null = null;
  /** Sprite dimensions inferred from cues (max x+w, max y+h) for correct scaling. */
  private spriteWidth = 0;
  private spriteHeight = 0;

  constructor(
    private findClipRepository: IClippingStudioFindClipRepository,
    private manager: ClippingStudioManagerService,
    private thumbnailVtt: TimelineThumbnailVttService,
  ) {
    effect(
      () => {
        const id = this.manager.projectId();
        if (id != null && id > 0) {
          this.loadRecentPrompts();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.loadThumbnailCuesIfNeeded();
  }

  get canGenerateClips(): boolean {
    const media = this.manager.state.media;
    return !!(media?.videoSlug?.trim());
  }

  /** Current project id from manager (signal); 0 when not loaded. */
  private get projectId(): number {
    return this.manager.projectId() ?? 0;
  }

  async onGenerate(): Promise<void> {
    if (
      this.isGenerating ||
      this.isGeneratingEdit ||
      !this.description?.trim() ||
      !this.canGenerateClips
    )
      return;
    const videoName = this.manager.state.media?.videoSlug ?? "";
    this.isGenerating = true;
    this.suggestedClips = [];
    this.suggestedEdit = null;
    this.errorMessage = null;
    try {
      this.suggestedClips = await this.findClipRepository.generateClips({
        description: this.description,
        duration: this.duration,
        videoName,
      });
      this.loadThumbnailCuesIfNeeded();
      if (this.description.trim()) {
        await this.findClipRepository.savePrompt(this.projectId, this.description);
        await this.loadRecentPrompts();
      }
    } catch (err) {
      console.error("[Find Clips] generateClips failed:", err);
      this.errorMessage = USER_FACING_ERROR_MESSAGE;
    } finally {
      this.isGenerating = false;
    }
  }

  async onGenerateEdit(): Promise<void> {
    if (
      this.isGenerating ||
      this.isGeneratingEdit ||
      !this.description?.trim() ||
      !this.canGenerateClips
    )
      return;
    const videoName = this.manager.state.media?.videoSlug ?? "";
    this.isGeneratingEdit = true;
    this.suggestedClips = [];
    this.suggestedEdit = null;
    this.errorMessage = null;
    try {
      const request = {
        description: this.description,
        duration: this.duration,
        videoName,
      };
      const clips = await this.findClipRepository.generateClips(request);
      this.suggestedClips = clips;
      this.suggestedEdit =
        this.findClipRepository.buildSuggestedEditFromClips(clips);
      this.loadThumbnailCuesIfNeeded();
      if (this.description.trim()) {
        await this.findClipRepository.savePrompt(this.projectId, this.description);
        await this.loadRecentPrompts();
      }
    } catch (err) {
      console.error("[Find Clips] generateFullEdit failed:", err);
      this.errorMessage = USER_FACING_ERROR_MESSAGE;
    } finally {
      this.isGeneratingEdit = false;
    }
  }

  onClear(): void {
    this.suggestedClips = [];
    this.suggestedEdit = null;
    this.errorMessage = null;
  }

  onSelectPrompt(prompt: string): void {
    if (!prompt?.trim()) return;
    this.description = prompt;
  }

  promptTrackBy(_index: number, prompt: string): string {
    return prompt;
  }

  onAddClipToTimeline(clip: FindClipSuggestedClip): void {
    const parsedMs = this.parseTimestamp(clip.timestamp);
    const durationMs = this.parseDuration(clip.duration);
    if (parsedMs == null || durationMs == null) return;

    const durationMsMedia = this.manager.state.media?.durationMs ?? 0;
    const startMs = parsedMs;
    const endMs = Math.min(durationMsMedia, parsedMs + durationMs);
    const alreadyExists = this.manager.state.clips.some(
      (c) => c.inMs === startMs && c.outMs === endMs,
    );
    if (alreadyExists) return;

    const inEditMode =
      this.manager.state.mode === "EDIT" &&
      (this.manager.state.clips?.length ?? 0) > 0;

    if (inEditMode) {
      this.manager.seek(this.manager.sourceToEditTimeMs(parsedMs));
      const clipId = this.manager.addSegmentFromInOut(clip.title, {
        inMs: startMs,
        outMs: endMs,
      });
      if (clipId) {
        this.segmentAdded.emit(clipId);
        this.manager.notifySegmentAddedFromPanel(clipId);
        this.manager.seek(this.manager.sourceToEditTimeMs(parsedMs));
      }
    } else {
      this.manager.seek(parsedMs);
      this.manager.markIn();
      this.manager.seek(endMs);
      this.manager.markOut();
      const clipId = this.manager.addSegmentFromInOut(clip.title);
      if (clipId) {
        this.segmentAdded.emit(clipId);
        this.manager.notifySegmentAddedFromPanel(clipId);
        this.manager.seek(parsedMs);
      }
    }
  }

  onAddEditToTimeline(): void {
    if (!this.suggestedEdit) return;
    this.manager.clearAllClips();
    for (const clip of this.suggestedClips) {
      this.onAddClipToTimeline(clip);
    }
  }

  get hasResults(): boolean {
    return this.suggestedClips.length > 0 || this.suggestedEdit != null;
  }

  get showEmptyState(): boolean {
    return (
      !this.hasResults && !this.isGenerating && !this.isGeneratingEdit
    );
  }

  private async loadRecentPrompts(): Promise<void> {
    this.promptsLoading.set(true);
    this.promptsError.set(null);
    try {
      const list = await this.findClipRepository.getRecentPrompts(this.projectId);
      const filtered = list.filter((p) => typeof p === "string" && p.trim());
      this.recentPrompts.set(filtered);
      this.manager.setFindClipRecentPrompts(filtered);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load recent prompts";
      this.promptsError.set(message);
      this.recentPrompts.set([]);
      this.manager.setFindClipRecentPrompts([]);
    } finally {
      this.promptsLoading.set(false);
    }
  }

  private parseTimestamp(ts: string): number | null {
    const parts = ts.split(":");
    if (parts.length < 3) return null;
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    const seconds = parseInt(parts[2], 10) || 0;
    return (hours * 3600 + minutes * 60 + seconds) * 1000;
  }

  private parseDuration(dur: string): number | null {
    const match = dur.match(/^(\d+)s$/);
    if (match) return parseInt(match[1], 10) * 1000;
    const mMatch = dur.match(/^(\d+)m\s*(\d+)s$/);
    if (mMatch)
      return (
        (parseInt(mMatch[1], 10) * 60 + parseInt(mMatch[2], 10)) * 1000
      );
    return null;
  }

  private loadThumbnailCuesIfNeeded(): void {
    const vttUrl =
      this.manager.state.media?.thumbnailsSpriteWebVTTUrl?.trim() ?? null;
    if (!vttUrl) {
      this.thumbnailCues = null;
      this.thumbnailVttUrl = null;
      return;
    }
    if (this.thumbnailVttUrl === vttUrl) return;
    this.thumbnailVttUrl = vttUrl;
    this.thumbnailVtt.getCues(vttUrl).then((cues) => {
      if (this.thumbnailVttUrl !== vttUrl) return;
      this.thumbnailCues = cues;
      if (cues.length > 0) {
        this.spriteWidth = Math.max(...cues.map((c) => c.x + c.w));
        this.spriteHeight = Math.max(...cues.map((c) => c.y + c.h));
      } else {
        this.spriteWidth = 0;
        this.spriteHeight = 0;
      }
    });
  }

  /**
   * Style for clip thumbnail from sprite. Sizes the div to one tile (cue.w x cue.h)
   * and sets background-size to full sprite so exactly one frame is visible.
   * Null when no media/sprite or no cue for clip timestamp.
   */
  getClipThumbnailStyle(
    clip: FindClipSuggestedClip,
  ): { [key: string]: string } | null {
    const spriteUrl = this.manager.state.media?.thumbnailsSpriteUrl;
    const cues = this.thumbnailCues;
    if (
      !spriteUrl ||
      !cues?.length ||
      this.spriteWidth <= 0 ||
      this.spriteHeight <= 0
    )
      return null;
    const timeMs = this.parseTimestamp(clip.timestamp);
    if (timeMs == null) return null;
    const eligible = cues.filter((c) => c.startMs <= timeMs);
    const cue = eligible.length ? eligible[eligible.length - 1] : cues[0];
    const scale = 1.45;
    const w = Math.round(cue.w * scale);
    const h = Math.round(cue.h * scale);
    return {
      width: `${w}px`,
      maxWidth: "100%",
      aspectRatio: `${cue.w}/${cue.h}`,
      height: "auto",
      backgroundImage: `url(${spriteUrl})`,
      backgroundPosition: `-${cue.x * scale}px -${cue.y * scale}px`,
      backgroundSize: `${Math.round(this.spriteWidth * scale)}px ${Math.round(this.spriteHeight * scale)}px`,
      backgroundRepeat: "no-repeat",
    };
  }
}
